/**
 * Booking Form Component
 * 
 * Collects customer and pet information for online booking.
 */

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

interface BookingFormData {
  phone: string;
  email: string;
  name: string;
  petName: string;
  petSpecies: string;
  petBreed: string;
  visitReason: string;
  notes: string;
}

interface BookingFormProps {
  onSubmit: (data: BookingFormData) => void;
  submitting: boolean;
  practiceName: string;
}

const SPECIES_OPTIONS = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
  { value: 'bird', label: 'Bird' },
  { value: 'rabbit', label: 'Rabbit' },
  { value: 'reptile', label: 'Reptile' },
  { value: 'other', label: 'Other' }
];

const VISIT_REASONS = [
  { value: 'wellness', label: 'Wellness / Annual Exam' },
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'sick', label: 'Sick Visit' },
  { value: 'injury', label: 'Injury' },
  { value: 'dental', label: 'Dental' },
  { value: 'grooming', label: 'Grooming' },
  { value: 'surgery', label: 'Surgery Consultation' },
  { value: 'follow-up', label: 'Follow-up Visit' },
  { value: 'other', label: 'Other' }
];

const BookingForm: React.FC<BookingFormProps> = ({
  onSubmit,
  submitting,
  practiceName
}) => {
  const [formData, setFormData] = useState<BookingFormData>({
    phone: '',
    email: '',
    name: '',
    petName: '',
    petSpecies: 'dog',
    petBreed: '',
    visitReason: 'wellness',
    notes: ''
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof BookingFormData, string>>>({});
  const [consentChecked, setConsentChecked] = useState(false);

  const validatePhone = (phone: string) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10;
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleChange = (field: keyof BookingFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Partial<Record<keyof BookingFormData, string>> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Valid phone number is required';
    }
    
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    
    if (!formData.petName.trim()) {
      newErrors.petName = 'Pet name is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Contact Information */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Your Information</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm text-gray-600 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
              placeholder="John Smith"
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1">{errors.name}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm text-gray-600 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
                placeholder="(555) 123-4567"
              />
              {errors.phone && (
                <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm text-gray-600 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
                placeholder="john@example.com"
              />
              {errors.email && (
                <p className="text-xs text-red-600 mt-1">{errors.email}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pet Information */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Pet Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="petName" className="block text-sm text-gray-600 mb-1">
                Pet Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="petName"
                value={formData.petName}
                onChange={(e) => handleChange('petName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.petName ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
                placeholder="Max"
              />
              {errors.petName && (
                <p className="text-xs text-red-600 mt-1">{errors.petName}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="petSpecies" className="block text-sm text-gray-600 mb-1">
                Species
              </label>
              <select
                id="petSpecies"
                value={formData.petSpecies}
                onChange={(e) => handleChange('petSpecies', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {SPECIES_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label htmlFor="petBreed" className="block text-sm text-gray-600 mb-1">
              Breed (optional)
            </label>
            <input
              type="text"
              id="petBreed"
              value={formData.petBreed}
              onChange={(e) => handleChange('petBreed', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Golden Retriever"
            />
          </div>
        </div>
      </div>

      {/* Visit Details */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Visit Details</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="visitReason" className="block text-sm text-gray-600 mb-1">
              Reason for Visit
            </label>
            <select
              id="visitReason"
              value={formData.visitReason}
              onChange={(e) => handleChange('visitReason', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {VISIT_REASONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="notes" className="block text-sm text-gray-600 mb-1">
              Additional Notes (optional)
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Any additional information..."
            />
          </div>
        </div>
      </div>

      {/* Consent */}
      <div className="border-t border-gray-200 pt-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">
            I consent to {practiceName} contacting me about this appointment request via phone, email, or text message.
          </span>
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting || !consentChecked}
        className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Submitting...
          </>
        ) : (
          'Request Appointment'
        )}
      </button>
      
      <p className="text-xs text-gray-400 text-center">
        Your information is secure and will only be used for booking purposes.
      </p>
    </form>
  );
};

export default BookingForm;
